// The crew of one scheduled Lodge activity, edited the way an activity's
// standard crew is (Scheduling → Activities): one row per position with a
// count, not one slot at a time. Pure — no React, no API. Used by the web
// console (ActivityScheduling.jsx) and the Lodge iOS app (schedulingStaffing.tsx)
// so the two cannot disagree about which places a lower count gives up.
//
//   crewRows(slots)          the activity's slots grouped into rows: same
//                            position name (any case) and same "logs as" = one
//                            row, in first-seen order. Each row keeps its slots.
//   crewChanges(rows)        what Save sends: { addSlots, removeSlotIds }. A cut
//                            removes OPEN places first, newest first, so people
//                            already assigned keep their place where possible.
//   crewWarnings(rows)       the assigned people a save would take off, by name.
//   crewSuggestions(types)   every position the lodge's activities use, most
//                            used first — the quick-add chips.
//   withStandardCrew(rows, positions)  tops each standard position up to its
//                            standard count; never takes a place away.

export interface CrewSlot { slotId: string; role: string; workType?: string | null; staffKey?: string | null; staffName?: string | null }
export interface CrewRow { key: string; role: string; workType: string; count: number; slots: CrewSlot[]; isNew: boolean }
export interface CrewPosition { role: string; headcount: number; workType?: string | null }

const norm = (role: string, workType?: string | null) => `${String(role || "").trim().toLowerCase()}|${workType || "other"}`;

let seq = 0;
export function newCrewRow(role = "", workType = "other", count = 1): CrewRow {
  seq += 1;
  return { key: `new:${seq}`, role, workType: workType || "other", count, slots: [], isNew: true };
}

export function crewRows(slots: CrewSlot[] = []): CrewRow[] {
  const rows: CrewRow[] = [];
  const byKey = new Map<string, CrewRow>();
  for (const s of slots) {
    const k = norm(s.role, s.workType);
    let row = byKey.get(k);
    if (!row) {
      row = { key: `slot:${k}`, role: s.role, workType: s.workType || "other", slots: [], count: 0, isNew: false };
      byKey.set(k, row); rows.push(row);
    }
    row.slots.push(s); row.count += 1;
  }
  return rows;
}

export function sameCrewPosition(row: { role: string; workType?: string | null }, role: string, workType?: string | null): boolean {
  return norm(row.role, row.workType) === norm(role, workType);
}

// The slots a row gives up when its count drops: open first (latest first), then filled.
export function slotsToRemove(row: CrewRow): CrewSlot[] {
  if (row.isNew) return [];
  const n = Math.max(0, row.slots.length - Math.max(0, Number(row.count) || 0));
  if (!n) return [];
  const open = row.slots.filter((s) => !s.staffKey).reverse();
  const filled = row.slots.filter((s) => s.staffKey).reverse();
  return [...open, ...filled].slice(0, n);
}

export function crewChanges(rows: CrewRow[] = []): { addSlots: CrewPosition[]; removeSlotIds: string[] } {
  const addSlots: CrewPosition[] = []; const removeSlotIds: string[] = [];
  for (const r of rows) {
    const count = Math.max(0, Math.round(Number(r.count) || 0));
    if (r.isNew) {
      if (String(r.role || "").trim() && count > 0) addSlots.push({ role: String(r.role).trim(), headcount: count, workType: r.workType || "other" });
      continue;
    }
    if (count > r.slots.length) addSlots.push({ role: r.role, headcount: count - r.slots.length, workType: r.workType || "other" });
    for (const s of slotsToRemove({ ...r, count })) removeSlotIds.push(s.slotId);
  }
  return { addSlots, removeSlotIds };
}

export function crewWarnings(rows: CrewRow[] = []): string[] {
  return rows.flatMap((r) => slotsToRemove(r).filter((s) => s.staffKey).map((s) => `${s.staffName || "Someone"} (${r.role})`));
}

export const crewTotal = (rows: CrewRow[] = []): number =>
  rows.reduce((n, r) => n + (r.isNew && !String(r.role || "").trim() ? 0 : Math.max(0, Number(r.count) || 0)), 0);

export function crewSuggestions(types: { positions?: CrewPosition[] }[] = [], max = 12): { role: string; workType: string }[] {
  const seen = new Map<string, { role: string; workType: string; n: number }>();
  for (const t of types) for (const p of t.positions || []) {
    const k = norm(p.role, p.workType);
    const cur = seen.get(k) || { role: String(p.role).trim(), workType: p.workType || "other", n: 0 };
    cur.n += 1; seen.set(k, cur);
  }
  return [...seen.values()].sort((a, b) => b.n - a.n || a.role.localeCompare(b.role)).slice(0, max).map(({ role, workType }) => ({ role, workType }));
}

// One more of a position: bumps its row, or adds a row (dropping a blank new row).
export function crewQuickAdd(rows: CrewRow[], role: string, workType?: string | null): CrewRow[] {
  const hit = rows.find((r) => sameCrewPosition(r, role, workType));
  if (hit) return rows.map((r) => (r === hit ? { ...r, count: (Number(r.count) || 0) + 1 } : r));
  return [...rows.filter((r) => !(r.isNew && !String(r.role).trim())), newCrewRow(role, workType || "other", 1)];
}

export function withStandardCrew(rows: CrewRow[], positions: CrewPosition[] = []): CrewRow[] {
  let out = [...rows];
  for (const p of positions) {
    const hit = out.find((r) => sameCrewPosition(r, p.role, p.workType));
    if (hit) out = out.map((r) => (r === hit ? { ...r, count: Math.max(Number(r.count) || 0, p.headcount) } : r));
    else out = [...out, newCrewRow(p.role, p.workType || "other", p.headcount)];
  }
  return out;
}
